import { useQuery } from 'react-query';

import { QueryEntity } from '../types';
import { RelativityListsResponse } from '../types/backend-dto-types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface UseGetRelativityListsOptions {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  page?: number;
  pageSize?: number;
  workspaceId?: number;
  search?: string;
}

const useGetRelativityLists = (options: UseGetRelativityListsOptions = {}) => {
  const { enabled = true, refetchOnWindowFocus = false, page = 1, pageSize = 25, workspaceId, search } = options;
  const { fetchConfigGET } = useGetFetchConfig();

  const getRelativityLists = async (): Promise<RelativityListsResponse> => {
    if (!workspaceId) {
      throw new Error('Workspace ID is required');
    }

    const searchParams = new URLSearchParams();
    searchParams.append('page', page.toString());
    searchParams.append('pageSize', pageSize.toString());
    if (search) {
      searchParams.append('search', search);
    }

    const url = `${APIBaseChronos}/api/relativity/workspaces/${workspaceId}/lists?${searchParams.toString()}`;
    const response = await fetch(url, fetchConfigGET);

    if (!response.ok) {
      throw new Error(`Failed to fetch Relativity lists: ${response.status} ${response.statusText}`);
    }

    return response.json();
  };

  return useQuery([QueryEntity.RelativityLists, workspaceId, page, pageSize, search], getRelativityLists, {
    enabled: enabled && !!workspaceId,
    refetchOnWindowFocus,
    keepPreviousData: true,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      if (error instanceof Error && error.message.includes('403')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

export default useGetRelativityLists;
